import { addMonths, subMonths } from "date-fns"
import React, { createContext, useContext, useMemo } from "react"
import { ICollectionGroup, type ICollectionDetails } from "~/hooks/useCollections"
import { startOfTodayUTC } from "~/utils/datetime"

type CollectionContextTypeInput = {
  collections: ICollectionDetails[]
  collectionGroups: { id: number; name: string }[]
  startDate?: Date | null
  endDate?: Date | null
}

export type ExtendedCollectionGroup = ICollectionGroup & {
  collectionType: "noos" | "mixed"
}

type CollectionContextType = {
  collections: ICollectionDetails[]
  collectionGroups: ExtendedCollectionGroup[]
  startDate: Date
  endDate: Date
}

const CollectionContext = createContext<CollectionContextType | undefined>(undefined)

export const CollectionProvider = ({
  children,
  value,
}: {
  children: React.ReactNode
  value: CollectionContextTypeInput
}) => {
  // Memoize to prevent re-computation on parent re-renders (e.g., tab changes)
  const outputValue = useMemo(() => {
    const collectionGroupsWithCollectionType = value.collectionGroups.map((group) => {
      const collectionsInGroup = value.collections.filter(
        (col) => col.collectionGroupId === group.id,
      )
      const collectionTypes = Array.from(
        new Set(collectionsInGroup.map((col) => col.collectionType)),
      )

      const isNoosGroup = collectionTypes.length === 1 && collectionTypes[0] === "noos"

      return {
        ...group,
        collectionType: isNoosGroup ? "noos" : "mixed",
      } as ExtendedCollectionGroup
    })

    return {
      collections: value.collections,
      collectionGroups: collectionGroupsWithCollectionType,
      startDate: value.startDate ?? subMonths(startOfTodayUTC(), 12),
      endDate: value.endDate ?? addMonths(startOfTodayUTC(), 12),
    }
  }, [value.collections, value.collectionGroups, value.startDate, value.endDate])

  return <CollectionContext.Provider value={outputValue}>{children}</CollectionContext.Provider>
}

export const useCollectionContext = () => {
  const context = useContext(CollectionContext)
  if (!context) {
    throw new Error("useCollectionContext must be used within a CollectionProvider")
  }
  return context
}
